// Pure helpers for the HSE "ANNEXURE - REFERENCES & STANDARDS" section.
//
// Annexure references are a flat, globally-shared list (`annexure_references`):
// each row is either a section heading (`type: "heading"`) or a data row
// (`type: "data"`). A data row belongs to a section via its `section_name`,
// which matches a heading's `section_name`. These helpers turn that flat list
// into per-section groups for rendering, and back into a section-ordered flat
// list for exporting. Kept side-effect free so they can be unit tested and so
// all three report surfaces (Saved / Preview / View) share one implementation.

// Normalise a section name for matching: tolerant of case and surrounding space
// so "ISO Standards" and "  iso standards " resolve to the same section.
export const normSection = (v) => (v || "").trim().toLowerCase();

// Drop annexures the current report has hidden (report-specific delete). The
// global rows are untouched; `deletedIds` lists ids hidden for THIS report.
export function filterVisibleReferences(references, deletedIds) {
  const hidden = Array.isArray(deletedIds) ? deletedIds : [];
  return (Array.isArray(references) ? references : []).filter(
    (ref) => !hidden.includes(ref.id)
  );
}

// Group visible references under their section headings. Each heading becomes a
// group containing the data rows whose section_name matches it. Data rows that
// match no heading fall into a trailing "Ungrouped" bucket (heading: null) so
// nothing is ever silently dropped. Heading order (and row order within a
// section) follows input order, which callers keep as id-ascending.
export function groupReferences(visibleReferences) {
  const refs = Array.isArray(visibleReferences) ? visibleReferences : [];
  const headings = refs.filter((r) => r.type === "heading");
  const dataRows = refs.filter((r) => r.type !== "heading");

  const groups = headings.map((h) => ({
    key: h.id,
    heading: h,
    section_name: h.section_name || "",
    rows: dataRows.filter(
      (d) => normSection(d.section_name) === normSection(h.section_name)
    ),
  }));

  const knownSections = new Set(headings.map((h) => normSection(h.section_name)));
  const orphanRows = dataRows.filter(
    (d) => !knownSections.has(normSection(d.section_name))
  );
  if (orphanRows.length) {
    groups.push({
      key: "__ungrouped__",
      heading: null,
      section_name: "",
      rows: orphanRows,
    });
  }

  return groups;
}

// Flatten groups back into a section-ordered list: each heading immediately
// followed by its rows. This is what the Word / Excel / PDF exporters consume,
// so the exported annexure matches the on-screen grouping instead of relying on
// raw insertion (id) order.
export function orderReferences(groups) {
  const out = [];
  (Array.isArray(groups) ? groups : []).forEach((g) => {
    if (g.heading) out.push(g.heading);
    g.rows.forEach((r) => out.push(r));
  });
  return out;
}
